
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import { FiArrowRight, FiCheckCircle } from 'react-icons/fi';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    projectType: 'Exterior Rendering',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      const response = await fetch('http://localhost:3001/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      const result = await response.json();

      if (result.success) {
        setIsSubmitted(true);
      } else {
        throw new Error(result.error || 'Failed to send message');
      }
    } catch (err) {
      console.error('Submission Error:', err);
      setError('Something went wrong. Please try again or email us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 md:py-32 bg-neutral-950 text-white relative overflow-hidden">
      <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-neutral-900/50 to-transparent pointer-events-none" />
      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }}>
            <h2 className="text-5xl md:text-6xl font-light mb-6">Let's build <br/><span className="font-serif italic font-semibold">together.</span></h2>
            <p className="text-neutral-400 mb-12 max-w-md text-lg font-light">
              Ready to bring your architectural vision to life? Contact us to discuss your next project and request a detailed proposal.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a href="mailto:info@daszeph.com" className="inline-flex items-center justify-center border border-white/30 px-8 py-4 text-sm uppercase tracking-widest hover:bg-white hover:text-black transition-all duration-300">
                Email Us
              </a>
              <a href="tel:6893322911" className="inline-flex items-center justify-center border border-white/30 px-8 py-4 text-sm uppercase tracking-widest hover:bg-white hover:text-black transition-all duration-300">
                Call Us
              </a>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8, delay: 0.2 }} className="bg-neutral-900 p-8 md:p-12 min-h-[400px] flex flex-col justify-center relative">
            <AnimatePresence mode="wait">
              {!isSubmitted ? (
                <motion.form key="form" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-8 w-full" onSubmit={handleSubmit}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div className="space-y-2">
                      <label className="text-xs uppercase tracking-widest text-neutral-400">Name</label>
                      <input required name="name" value={formData.name} onChange={handleChange} type="text" className="w-full bg-transparent border-b border-neutral-700 py-2 text-white focus:outline-none focus:border-white transition-colors uppercase text-sm" placeholder="John Doe" />
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs uppercase tracking-widest text-neutral-400">Email</label>
                      <input required name="email" value={formData.email} onChange={handleChange} type="email" className="w-full bg-transparent border-b border-neutral-700 py-2 text-white focus:outline-none focus:border-white transition-colors text-sm" placeholder="john@company.com" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs uppercase tracking-widest text-neutral-400">Project Type</label>
                    <select name="projectType" value={formData.projectType} onChange={handleChange} className="w-full bg-transparent border-b border-neutral-700 py-2 text-neutral-300 focus:outline-none focus:border-white transition-colors appearance-none text-sm">
                      <option className="bg-neutral-900">Exterior Rendering</option>
                      <option className="bg-neutral-900">Interior Visualization</option>
                      <option className="bg-neutral-900">Animation</option>
                      <option className="bg-neutral-900">Other</option>
                    </select>
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs uppercase tracking-widest text-neutral-400">Message</label>
                    <textarea required name="message" value={formData.message} onChange={handleChange} rows="4" className="w-full bg-transparent border-b border-neutral-700 py-2 text-white focus:outline-none focus:border-white transition-colors resize-none text-sm" placeholder="Tell us about your vision..."></textarea>
                  </div>

                  {error && <p className="text-red-400 text-xs italic">{error}</p>}

                  <button disabled={isSubmitting} className="flex items-center gap-4 text-sm uppercase tracking-widest text-white border border-white/30 px-8 py-4 hover:bg-white hover:text-black transition-all duration-300 w-full justify-center group disabled:opacity-50 disabled:cursor-not-allowed">
                    {isSubmitting ? 'Sending...' : 'Send Inquiry'}
                    {!isSubmitting && <SafeIcon icon={FiArrowRight} className="group-hover:translate-x-1 transition-transform" />}
                  </button>
                </motion.form>
              ) : (
                <motion.div key="success" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="text-center py-12">
                  <SafeIcon icon={FiCheckCircle} className="w-16 h-16 text-white mx-auto mb-6" />
                  <h3 className="text-2xl font-light mb-4">Thank you for your inquiry.</h3>
                  <p className="text-neutral-400 font-light"> Your message has been sent successfully. We'll be in contact within 24 hours. </p>
                  <button onClick={() => setIsSubmitted(false)} className="mt-8 text-xs uppercase tracking-widest text-neutral-500 hover:text-white transition-colors underline underline-offset-4">
                    Send another message
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
